import { useEffect, useRef, useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import SimpleBar from 'simplebar-react';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { InputCountry } from '@/components/InputCountry/InputCountry';
import Loader from '@/components/Loader';
import Box from '@/design_system/Box';
import Dialog from '@/design_system/Dialog';
import InputSearch from '@/design_system/InputSearch';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import IconPhone from '@/icons/Phone.svg';
import IconPlace from '@/icons/Place.svg';
import { formatStreet, formatZip } from '@/models/address';
import { formatPhone } from '@/models/partials/phone';
import { useInfiniteStores } from '@/models/store';
import { useCurrentOrganization } from '@/services/auth';
import useViewPort from '@/utils/useViewport';

export const DropOffStoresDialog = ({
  isOpen,
  onOpenChange,
}: {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
}) => {
  const { isMobile } = useViewPort();
  const { t } = useLingui();
  const [organization] = useCurrentOrganization();
  const organizationName = organization?.name;
  const loadNextPageTriggerRef = useRef(null);

  const storesPerPage = 24; // To provide a cool UX

  const [country, setCountry] = useState<string | null>(null);
  const [city, setCity] = useState<string>();

  const { data, fetchNextPage, isLoading } = useInfiniteStores(
    {
      limit: storesPerPage,
      internal: true,
      embedded: true,
      country: country ?? undefined,
      search: city ?? undefined,
    },
    { enabled: isOpen }
  );

  const stores = data?.pages.flatMap((page) => page.stores) ?? [];

  useEffect(() => {
    if (loadNextPageTriggerRef.current === null) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          fetchNextPage();
        }
      },
      {
        // Root is the viewport
        root: null,
        threshold: 1.0,
      }
    );

    observer.observe(loadNextPageTriggerRef.current);

    return () => {
      observer.disconnect();
    };
  }, [loadNextPageTriggerRef, fetchNextPage, isOpen]);

  return (
    <Dialog
      title={<Trans id="client.drop-off-stores.dialog.title">{organizationName} stores</Trans>}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      style={{
        width: '50rem',
      }}
    >
      <main>
        <Stack gap="1rem">
          <Paragraph>
            <Trans id="client.drop-off-stores.dialog.subtitle">
              Below is a list of authorized {organizationName} stores where you can drop off your
              item for shipment handling
            </Trans>
          </Paragraph>
          <Stack row={!isMobile} gap="1rem">
            <InputCountry
              isClearable
              style={{ flex: 1 }}
              aria-label={t({
                id: 'client.drop-off-stores.dialog.country.label',
                message: 'Country',
              })}
              placeholder={t({
                id: 'client.drop-off-stores.dialog.country.placeholder',
                message: 'Country: All',
              })}
              value={country}
              onChange={setCountry}
            />
            <InputSearch
              style={{ flex: 1 }}
              aria-label={t({ id: 'client.drop-off-stores.dialog.city.label', message: 'City' })}
              placeholder={t({
                id: 'client.drop-off-stores.dialog.city.placeholder',
                message: 'Search for a city...',
              })}
              value={city}
              onChange={setCity}
            />
          </Stack>
          <Stack
            style={{
              height: isMobile ? undefined : '30rem',
              minHeight: isMobile ? '20rem' : undefined,
            }}
          >
            {isLoading && (
              <Stack gap="16px" alignItems="center" justifyContent="center" style={{ flex: 1 }}>
                <Loader />
                <Paragraph>
                  <Trans id="client.drop-off-stores.dialog.loading">Search in progress...</Trans>
                </Paragraph>
              </Stack>
            )}
            {!isLoading && stores.length === 0 && (
              <EmptyState
                subtitle={
                  <span>
                    <Trans id="client.drop-off-stores.dialog.no-stores">
                      No results are matching your criteria.
                      <br />
                      Please try with different keywords or filters
                    </Trans>
                  </span>
                }
                illustration={<IllustrationNoResults />}
              />
            )}
            {stores.length > 0 && (
              <SimpleBar
                style={{
                  // eslint-disable-next-line lingui/no-unlocalized-strings
                  margin: isMobile ? undefined : '0 -2rem -1.5rem -2rem',
                  padding: isMobile ? undefined : '0 2rem',
                  height: isMobile ? undefined : '30rem',
                }}
                autoHide={false}
              >
                <Stack
                  gap="1rem"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(14rem, 1fr))',
                  }}
                >
                  {stores.map((store) => (
                    <Box key={store.id} padding="16px">
                      <Stack gap="0.5rem">
                        <Paragraph weight="medium">{store.name}</Paragraph>
                        <Stack row gap="0.5rem" flexWrap="nowrap">
                          <IconPlace end />
                          {!!store.address && (
                            <Stack style={{ overflow: 'auto' }}>
                              <InlineText size="200" ellipsis title={formatStreet(store.address)}>
                                {formatStreet(store.address)}
                              </InlineText>
                              <InlineText size="200" ellipsis title={formatZip(store.address)}>
                                {formatZip(store.address)}
                              </InlineText>
                            </Stack>
                          )}
                        </Stack>
                        {store.contactInfo?.phone && (
                          <Stack row gap="0.5rem">
                            <IconPhone />
                            <Paragraph size="200">{formatPhone(store.contactInfo.phone)}</Paragraph>
                          </Stack>
                        )}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                <div ref={loadNextPageTriggerRef} />
              </SimpleBar>
            )}
          </Stack>
        </Stack>
      </main>
    </Dialog>
  );
};
